import { useCallback } from 'react';
import cn from 'clsx';
import { mapSearchStateToAiSearchContext } from '@pkgs/components/utils/tracking';
import { useMapVisibilityStore } from '@pkgs/components/providers/MapVisibilityStoreProvider';
import { useSearchModuleTracking, getConsentContext } from '@pkgs/components/hooks/useSearchModuleTracking';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { WORK_EDITION_IMAGE_URL } from '@pkgs/components/consts/consts';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Button } from '@ev-tech/ui-core-components/Button';
import {
  type BusinessDivisionType,
  getAiSearchContext,
  getDivisionContext,
  getNavigationContext,
  trackNavigationEvent,
} from '@ev-tech/snowplow-library';
import { Language } from '@ev-tech/search-modules-api';

import styles from './WorkEditionCtaCard.module.css';

type WorkEditionCtaCardProps = {
  className?: string;
};

export const WorkEditionCtaCard = ({ className }: WorkEditionCtaCardProps) => {
  const {
    t,
    ImageComponent,
    options: { language },
  } = useAppContext();
  const { pageContext, shopContext, mapABTestContext } = useSearchModuleTracking();
  const isMapToggledOn = useMapVisibilityStore((state) => state.isMapActive);
  const objectTypeCategory = useSearchStore((state) => state.objectTypeCategory);
  const preferences = useSearchStore((state) => state.preferences);
  const searchMode = useSearchStore((state) => state.searchMode);

  const href = language === Language.de ? 'https://ev-workedition.de/de' : 'https://ev-workedition.de/en';

  const handleClick = useCallback(() => {
    trackNavigationEvent({
      screenName: '',
      contexts: [
        getNavigationContext({
          element_id: 'tile_secondary_work-edition',
          target_url: href,
        }),
        pageContext,
        shopContext,
        getDivisionContext({
          name: objectTypeCategory as BusinessDivisionType,
        }),
        getAiSearchContext(
          mapSearchStateToAiSearchContext({
            preferences,
            isAiSearchEnabled: searchMode === SearchMode.AI,
          }),
        ),
        getConsentContext(),
        mapABTestContext(isMapToggledOn),
      ],
    });
  }, [href, pageContext, objectTypeCategory, shopContext, preferences, searchMode, mapABTestContext, isMapToggledOn]);

  return (
    <article className={cn(styles.article, className)} data-testid="search-components_cta-card_work-edition">
      <ImageComponent src={WORK_EDITION_IMAGE_URL} alt="Work Edition" width={150} height={70} />
      <div className={styles.textContainer}>
        <Headline className={styles.title} label="Engel & Völkers Work Edition" size="h5" />
        <Copy text={t('search.searchAlert.ctaCard.workEdition.text')} />
      </div>

      <Button
        as="a"
        href={href}
        target="_blank"
        className={styles.button}
        size="medium"
        variant="primary"
        label={t('search.searchAlert.ctaCard.workEdition.button')}
        data-testid="search-components_cta-card_work-edition_button"
        onClick={handleClick}
      />
    </article>
  );
};
